import I18n from "i18n-js";
import React, { useEffect } from "react";
import { func, shape } from "prop-types";
import { connect } from "react-redux";

import { ROUTE_INSTALLATIONS, ROUTE_INSTALLATION_EDIT } from "../../../routes";
import { getInstallationInfos } from "../actions/installationsActions";
import { getNomenclatures } from "../../nomenclatures/actions/nomenclaturesActions";
import { getClientInfo } from "../../company/actions/clientsActions";
import { getSiteInfos } from "../../company/actions/sitesActions";

import ClientUtils from "../../company/utils/ClientUtils";
import FormLayout from "../../common/components/layout/FormLayout";
import InstallationInterventionList from "./Installation/components/InstallationInterventionList";
import InstallationAnalysesList from "./Installation/components/InstallationAnalysesList";
import InstallationComponentListView from "./Installation/components/InstallationComponentListView";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import pathToRegexp from "path-to-regexp";
import classNames from "classnames";
import NoData from "../../common/components/elements/NoData";

Installation.propTypes = {
  installation: shape(),
  nomenclatures: shape(),
  client: shape(),
  site: shape(),
  getInstallationInfos: func.isRequired,
  getNomenclatures: func.isRequired,
  getSiteInfos: func.isRequired,
  getClientInfo: func.isRequired,
  match: shape().isRequired,
};

function formatDate(date) {
  const [year, month, day] = date.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
}

function Installation({
  installation = undefined,
  nomenclatures = undefined,
  client = undefined,
  site = undefined,
  getInstallationInfos,
  getNomenclatures,
  getSiteInfos,
  getClientInfo,
  match,
}) {
  const { uuid } = match.params;
  const editRoute = pathToRegexp.compile(ROUTE_INSTALLATION_EDIT);

  const inversedBooleanIndic = (fail, warn = null) => {
    const doWarn = true === warn;
    return (
      <i
        className={classNames({
          fa: true,
          "text-success": !fail && !doWarn,
          "text-danger": fail,
          "text-warning": !fail && doWarn,
          "fa-check": !fail && !doWarn,
          "fa-times": fail,
          "fa-warning": !fail && doWarn,
        })}
        aria-hidden="true"
      />
    );
  };

  useEffect(() => {
    if (installation && installation.uuid === uuid) {
      return;
    }
    getNomenclatures();
    getInstallationInfos(uuid);
  }, [installation, match, getNomenclatures, getInstallationInfos, uuid]);

  useEffect(() => {
    if (!installation) return;
    const { site, client } = installation;

    if (installation && site && site.uuid) {
      getSiteInfos(site.uuid);
    }

    if (installation && client && client.uuid) {
      getClientInfo(client.uuid);
    }
  }, [installation, getSiteInfos, getClientInfo]);

  if (
    installation === undefined ||
    nomenclatures === undefined ||
    client === undefined ||
    site === undefined
  ) {
    return <div className="loader" />;
  }

  if (
    installation === null ||
    nomenclatures === null ||
    client === null ||
    site === null
  ) {
    return <p>{I18n.t("common.loadingApiError")}</p>;
  }

  return (
    <FormLayout
      title={I18n.t("pages.installations.view.title")}
      backRoute={ROUTE_INSTALLATIONS}
      forceRoute={true}
      size={FormLayout.LARGE}
      actions={
        <Link
          className="btn btn--secondary back mb-0 ms-1"
          to={editRoute({ uuid })}
        >
          Editer
        </Link>
      }
    >
      <div style={{ display: "flex" }}>
        <div className="card card-block card-half border-end-none border-bottom-none">
          <div className="detail-header">
            <div>{I18n.t("pages.installations.view.client:header")}</div>
            <div className="detail-header-band"></div>
          </div>
          <dl className="row align-center">
            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.client:label")}
            </dt>
            <dd className="col-sm-9">
              {ClientUtils.renderLabel(installation.client)}
            </dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.attestation-number:label")}
            </dt>
            <dd className="col-sm-9">
              {client?.certificateOfProfessionalCompetence?.number ??
                <NoData />}
            </dd>
            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.siret:label")}
            </dt>
            <dd className="col-sm-9">{client.siret}</dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.vat-number:label")}
            </dt>
            <dd className="col-sm-9">{client.vatCode ?? <NoData />}</dd>
          </dl>

          <div className="detail-header">
            <div>{I18n.t("pages.installations.view.site:header")}</div>
            <div className="detail-header-band"></div>
          </div>

          <dl className="row align-center">
            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.site:label")}
            </dt>
            <dd className="col-sm-9">{installation.site.designation}</dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.site-address:label")}
            </dt>
            <dd className="col-sm-9">
              {ClientUtils.renderAddress(site.address)}
            </dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.siret:label")}
            </dt>
            <dd className="col-sm-9">{site.siret}</dd>
          </dl>

          <div className="detail-header">
            <div>
              {I18n.t("pages.installations.view.identification:header")}
            </div>
            <div className="detail-header-band"></div>
          </div>
          <dl className="row align-center">
            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.id:label")}
            </dt>
            <dd className="col-sm-9">{installation.externalId}</dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.designation:label")}
            </dt>
            <dd className="col-sm-9">{installation.designation}</dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.mark:label")}
            </dt>
            <dd className="col-sm-9">{installation.mark}</dd>

            <dt className="col-sm-3">
              {I18n.t("pages.installations.view.barcode:label")}
            </dt>
            <dd className="col-sm-9">{installation.barcode ?? <NoData />}</dd>
          </dl>
        </div>
        <div className="card card-block card-half border-bottom-none">
          <div className="detail-header">
            <div>
              {I18n.t("pages.installations.view.technical-data:header")}
            </div>
            <div className="detail-header-band"></div>
          </div>
          <dl className="row align-center">
            <dt className="col-sm-6">
              {I18n.t("pages.installations.view.last-control-date:label")}
            </dt>
            <dd className="col-sm-6">
              {installation.lastLeakDetectionDate
                ? formatDate(installation.lastLeakDetectionDate)
                : inversedBooleanIndic(true)}
            </dd>

            <dt className="col-sm-6">
              {I18n.t(
                "pages.installations.view.integrated-leak-detection:label"
              )}
            </dt>
            <dd className="col-sm-6">
              {inversedBooleanIndic(!installation.integratedLeakDetector)}
            </dd>
            <dt className="col-sm-6" style={{ fontWeight: "700" }}>
              {I18n.t("pages.installations.view.cooling-fluid:label")}
            </dt>
            <dd className="col-sm-6">
              {installation.fluid.designation ?? <NoData />}
            </dd>

            <dt className="col-sm-6">
              {I18n.t("pages.installations.view.current-load:label")}
            </dt>
            <dd className="col-sm-6">{installation.currentLoad}</dd>

            <dt className="col-sm-6">
              {I18n.t("pages.installations.view.nominal-load:label")}
            </dt>
            <dd className="col-sm-6">{installation.nominalLoad}</dd>
          </dl>
        </div>
      </div>
      <div className="card card-block">
        <div className="detail-header">
          <div>{I18n.t("pages.installations.view.components:header")}</div>
          <div className="detail-header-band"></div>
        </div>
        <InstallationComponentListView
          components={installation.components}
          nomenclaturesList={nomenclatures}
        />
        <div className="detail-header mb-0">
          <div>{I18n.t("pages.installations.view.interventions:header")}</div>
          <div className="detail-header-band"></div>
        </div>
        <InstallationInterventionList
          installation={installation}
          interventions={installation.interventions}
        />
        <div className="detail-header">
          <div>{I18n.t("pages.installations.view.analyses:header")}</div>
          <div className="detail-header-band"></div>
        </div>
        <InstallationAnalysesList analyses={installation.analyses} />
      </div>
    </FormLayout>
  );
}

const mapStateToProps = (state) => ({
  installation: state.installations.info,
  nomenclatures: state.nomenclatures.nomenclatures,
  client: state.client.info,
  site: state.site.infos,
});

const mapDispatchToProps = (dispatch) => ({
  getInstallationInfos: (uuid) => dispatch(getInstallationInfos(uuid)),
  getNomenclatures: () => dispatch(getNomenclatures()),
  getClientInfo: (uuid) => dispatch(getClientInfo(uuid)),
  getSiteInfos: (uuid) => dispatch(getSiteInfos(uuid)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Installation);
